import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { deliverableTypeForArrayKey, normalizePlatformFeatureValue } from '@pages/campaigns/brief/brief-mapping.util';
import { IBriefValidationMessage } from '@pages/campaigns/brief/brief.interface';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { camelCase } from 'lodash';
import { IBriefWarningsDialogData } from './brief-warnings-dialog.interface';

@Component({
  imports: [MatButtonModule, TranslateModule, SideDialogComponent],
  selector: 'app-brief-warnings-dialog',
  templateUrl: './brief-warnings-dialog.component.html',
})
export class BriefWarningsDialogComponent {
  public readonly data = inject<IBriefWarningsDialogData>(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<BriefWarningsDialogComponent, boolean>);
  private readonly translate = inject(TranslateService);

  // The backend's own warning.message is raw English text - build our own translated copy from
  // its params instead, matching the 'downgraded_to_custom' shape the API actually emits (falls
  // back to the raw message for any other warning code or missing params). params went through
  // the same deepCamelCaseKeys pass as the rest of the response (see brief.service.ts), so its
  // key is 'platformFeature', not the API's raw 'platform_feature'.
  public getWarningMessage(warning: IBriefValidationMessage): string {
    if (warning.code !== 'downgraded_to_custom') {
      return warning.message;
    }

    const deliverableType = deliverableTypeForArrayKey(camelCase(warning.params['deliverable'] as string));
    const platformFeature = normalizePlatformFeatureValue(warning.params['platformFeature']);

    if (!deliverableType || !platformFeature) {
      return warning.message;
    }

    return this.translate.instant('dialogs.createCampaignDialog.briefWizard.warningsDialog.downgradedToCustom', {
      deliverable: this.translate.instant(`shared.deliverableTypes.${deliverableType}`),
      platformFeature: this.translate.instant(`shared.platformFeatures.${platformFeature}`),
    });
  }

  // Mirrors BriefWizardDialogComponent's currentCtaTemplateName - resolves the warning's own CTA
  // template (from its path) to a display name, falling back to the same "Deliverable group N"
  // translation when that template has no name yet. Null when the warning isn't scoped to a
  // template at all.
  public getWarningTemplateName(warning: IBriefValidationMessage): string | null {
    const templateIndex = this.templateIndexFromWarning(warning);

    if (templateIndex === null) {
      return null;
    }

    const ctaTemplates = (this.data.campaign['ctaTemplates'] as Record<string, unknown>[]) ?? [];
    const name = ctaTemplates[templateIndex]?.['name'] as string | null;

    if (name) {
      return name;
    }

    return this.translate.instant('dialogs.createCampaignDialog.briefWizard.deliverableGroupFallback', {
      index: templateIndex + 1,
    });
  }

  private templateIndexFromWarning(warning: IBriefValidationMessage): number | null {
    const [rootKey, templateIndex] = warning.pathTokens;

    return rootKey === 'ctaTemplates' && typeof templateIndex === 'number' ? templateIndex : null;
  }

  public onContinueClick(): void {
    this.dialogRef.close(true);
  }

  public onBackClick(): void {
    this.dialogRef.close(false);
  }
}
